import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponse, PickerIdsSchema } from './pagination.js';
import { IsoDateSchema, MessageSchema, MoneyStringSchema } from './primitives.js';
import { HR_DEPARTMENTS } from './hr.js';
const c = initContract();
/**
 * Recruitment (HR-05): "candidate pipeline → creates Employee on hire".
 *
 * Hiring creates a REAL employee in the shared master (the staging-list
 * prototype defect — "the largest single gap in this module" — is not
 * built), pre-filled per BR-HR-05-05: name, email, phone, the requisition's
 * department and job title, the offer's salary and start date.
 *
 * Approving/opening a requisition is `hr.approve` checked where the change
 * is written (BR-HR-05-15) — a commitment to spend money on a salary, never
 * a rendering preference (16.5.2a). Scoring is an interviewer's OWN record
 * behind the ordinary write permission (BR-HR-05-16). Keep-on-file is a
 * retention decision recorded explicitly on every rejection (BR-HR-05-17).
 *
 * Interviews are TASKS in the shared engine (BR-HR-05-08), related_kind
 * 'candidate' — on the interviewer's own list beside everything else.
 */
export const CANDIDATE_STAGES = [
    'applied',
    'screening',
    'interview',
    'offer',
    'hired',
    'rejected',
];
/** BR-HR-05-02: the four stages a candidate can still be worked on in. */
export const ACTIVE_CANDIDATE_STAGES = ['applied', 'screening', 'interview', 'offer'];
export const CANDIDATE_SOURCES = [
    'job_board',
    'referral',
    'linkedin',
    'agency',
    'walk_in',
    'other',
];
export const REJECTION_REASONS = [
    'not_qualified',
    'salary_mismatch',
    'withdrew',
    'position_filled',
    'other',
];
export const REQUISITION_STATUSES = [
    'draft',
    'requested',
    'approved',
    'open',
    'on_hold',
    'closed',
    'filled',
];
export const SCORECARD_ROUNDS = ['phone_screen', 'technical', 'panel', 'final'];
export const OFFER_STATUSES = ['sent', 'accepted', 'declined', 'negotiating'];
export const EMPLOYMENT_TYPES = ['full_time', 'part_time', 'contract', 'internship'];
export const RECRUITMENT_MESSAGES = {
    /** BR-HR-05-15. */
    approveDenied: 'You do not have permission to approve a hiring requisition. Ask an administrator to grant it.',
    /**
     * BR-HR-05-04 — ERR-HR-05-01 (the grey Reject button) and ERR-HR-05-02 (a
     * card dragged onto Rejected, which opens the reason panel instead of moving
     * it). One gate, reached two ways.
     */
    reasonRequired: 'Choose a rejection reason.',
    /** BR-HR-05-02. */
    notActive: 'This candidate is no longer active.',
    /** §13's confirmation shape. */
    hired: (code) => `Employee ${code} created — pre-filled from candidate`,
    interviewScheduled: 'Interview scheduled via the Task engine',
    /** BR-HR-05-12. */
    approveBeforeOpen: 'A requisition must be approved before it is opened.',
    /** Q-HR-05-2's warning. */
    openingsFilled: (openings) => `This requisition's ${openings} opening${openings === 1 ? ' is' : 's are'} already filled.`,
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const RequisitionSchema = z
    .object({
    id: z.uuid(),
    code: z.string(),
    jobTitle: z.string(),
    department: z.enum(HR_DEPARTMENTS),
    hiringManagerEmployeeId: z.uuid().nullable(),
    hiringManagerName: z.string().nullable(),
    openings: z.number().int(),
    employmentType: z.enum(EMPLOYMENT_TYPES),
    /** INTERNAL ONLY (BR-HR-05-07) — hr.view is the internal boundary. */
    salaryMin: z.string().nullable(),
    salaryMax: z.string().nullable(),
    status: z.enum(REQUISITION_STATUSES),
    requestedBy: z.uuid().nullable(),
    approvedBy: z.uuid().nullable(),
    approvedAt: z.string().nullable(),
    candidateCount: z.number().int(),
    hiredCount: z.number().int(),
    createdAt: z.string(),
})
    .strict();
export const ScorecardSchema = z
    .object({
    id: z.uuid(),
    round: z.enum(SCORECARD_ROUNDS),
    rating: z.number().int().min(1).max(5),
    interviewerId: z.uuid().nullable(),
    notes: z.string().nullable(),
    createdAt: z.string(),
})
    .strict();
export const CandidateSchema = z
    .object({
    id: z.uuid(),
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    requisitionId: z.uuid(),
    requisitionTitle: z.string(),
    source: z.enum(CANDIDATE_SOURCES),
    referredByEmployeeId: z.uuid().nullable(),
    resume: z.string().nullable(),
    stage: z.enum(CANDIDATE_STAGES),
    talentPool: z.boolean(),
    rejectionReason: z.enum(REJECTION_REASONS).nullable(),
    offerSalary: z.string().nullable(),
    offerStartDate: z.string().nullable(),
    offerStatus: z.enum(OFFER_STATUSES).nullable(),
    offerLetter: z.string().nullable(),
    hiredEmployeeId: z.uuid().nullable(),
    /** BR-HR-05-13: mean of the scorecards, one decimal; null with none. */
    averageRating: z.string().nullable(),
    scorecards: ScorecardSchema.array(),
    createdAt: z.string(),
})
    .strict();
export const RequisitionWriteSchema = z
    .object({
    jobTitle: z.string().trim().min(1).max(120),
    department: z.enum(HR_DEPARTMENTS),
    hiringManagerEmployeeId: z.uuid().nullable().optional(),
    openings: z.number().int().min(1),
    employmentType: z.enum(EMPLOYMENT_TYPES).optional(),
    salaryMin: MoneyStringSchema.optional(),
    salaryMax: MoneyStringSchema.optional(),
})
    .strict();
export const CandidateWriteSchema = z
    .object({
    name: z.string().trim().min(1).max(160),
    email: z.string().trim().min(3).max(200),
    phone: z.string().trim().max(40).optional(),
    requisitionId: z.uuid(),
    source: z.enum(CANDIDATE_SOURCES).optional(),
    referredByEmployeeId: z.uuid().optional(),
    resume: z.string().trim().max(200).optional(),
})
    .strict();
const requisitionParams = z.object({ requisitionId: z.uuid() }).strict();
const candidateParams = z.object({ candidateId: z.uuid() }).strict();
export const recruitmentContract = c.router({
    listRequisitions: {
        method: 'GET',
        path: '/api/hr/requisitions',
        query: pagedQuery({}),
        responses: {
            200: pagedResponse('requisitions', RequisitionSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The jobs (hr.view), PAGED (NFR-42) — the per-requisition candidate tallies are SQL aggregates over the page, not a fold over every candidate in the tenant. Salary ranges are internal-only and stop at this permission boundary.',
    },
    createRequisition: {
        method: 'POST',
        path: '/api/hr/requisitions',
        body: RequisitionWriteSchema,
        responses: { 201: RequisitionSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema },
        summary: 'Open a requisition as Draft (hr.create). Zero openings and max<min are refused (§15).',
    },
    transitionRequisition: {
        method: 'POST',
        path: '/api/hr/requisitions/:requisitionId/transition',
        pathParams: requisitionParams,
        body: z
            .object({ to: z.enum(['requested', 'approved', 'open', 'on_hold', 'closed']) })
            .strict(),
        responses: {
            200: RequisitionSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Submit/Hold/Close need hr.edit; APPROVE and OPEN need hr.approve, checked where the change is written (BR-HR-05-15) — approving stamps the real approver (BR-HR-05-12). Open only from Approved.',
    },
    listCandidates: {
        method: 'GET',
        path: '/api/hr/candidates',
        query: pagedQuery({
            stage: z.enum(CANDIDATE_STAGES).optional(),
            requisitionId: z.uuid().optional(),
            source: z.enum(CANDIDATE_SOURCES).optional(),
            /**
             * The candidate a screen is ALREADY holding, resolved by id and by
             * nothing else. §7's board is one paged read per stage column, so the
             * DETAIL card cannot look its row up in an array of everybody — and
             * it must survive that row moving to another column, which is the one
             * thing the board's own action does.
             */
            ids: PickerIdsSchema.optional(),
        }),
        responses: {
            200: pagedResponse('candidates', CandidateSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The pipeline/table (hr.view), PAGED (NFR-42), with the stage filter the prototype wired and never drew — in SQL, where a LIMIT can be trusted.',
    },
    createCandidate: {
        method: 'POST',
        path: '/api/hr/candidates',
        body: CandidateWriteSchema,
        responses: {
            201: CandidateSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'New application (hr.create): name, email, requisition (BR-HR-05-01).',
    },
    moveCandidate: {
        method: 'POST',
        path: '/api/hr/candidates/:candidateId/move',
        pathParams: candidateParams,
        body: z
            .object({
            /** Advance omitted target = one stage forward (BR-HR-05-03). */
            to: z.enum(['applied', 'screening', 'interview', 'offer']).optional(),
        })
            .strict(),
        responses: {
            200: z.object({ candidate: CandidateSchema, message: z.string().nullable() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Advance one stage (or drag to a named ACTIVE stage — backwards allowed deliberately, §7). From Offer, Advance means HIRE and goes through /hire. hr.edit.',
    },
    rejectCandidate: {
        method: 'POST',
        path: '/api/hr/candidates/:candidateId/reject',
        pathParams: candidateParams,
        body: z
            .object({
            reason: z.enum(REJECTION_REASONS),
            /** BR-HR-05-17: recorded explicitly on EVERY rejection. */
            talentPool: z.boolean(),
        })
            .strict(),
        responses: {
            200: CandidateSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Reject — reason ALWAYS required, by button or by drag (BR-HR-05-04). hr.edit.',
    },
    setTalentPool: {
        method: 'POST',
        path: '/api/hr/candidates/:candidateId/talent-pool',
        pathParams: candidateParams,
        body: z.object({ talentPool: z.boolean() }).strict(),
        responses: {
            200: CandidateSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Change the keep-on-file decision AFTER rejection (BR-HR-05-11/-17 — the prototype could not). hr.edit.',
    },
    addScorecard: {
        method: 'POST',
        path: '/api/hr/candidates/:candidateId/scorecards',
        pathParams: candidateParams,
        body: z
            .object({
            round: z.enum(SCORECARD_ROUNDS),
            rating: z.number().int().min(1).max(5),
            notes: z.string().trim().max(2000).optional(),
        })
            .strict(),
        responses: {
            201: CandidateSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'An interviewer’s OWN record — always available on an active candidate behind ordinary hr.edit (BR-HR-05-16); the average moves with it (BR-HR-05-13).',
    },
    saveOffer: {
        method: 'PUT',
        path: '/api/hr/candidates/:candidateId/offer',
        pathParams: candidateParams,
        body: z
            .object({
            salary: MoneyStringSchema,
            startDate: IsoDateSchema,
            status: z.enum(OFFER_STATUSES),
            letter: z.string().trim().max(200).optional(),
        })
            .strict(),
        responses: {
            200: CandidateSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Save the offer — itself a stage transition: moves the candidate to Offer unless hired/rejected (BR-HR-05-09). hr.edit.',
    },
    hire: {
        method: 'POST',
        path: '/api/hr/candidates/:candidateId/hire',
        pathParams: candidateParams,
        body: z.object({}).strict().optional(),
        responses: {
            201: z
                .object({ candidate: CandidateSchema, employeeId: z.uuid(), employeeCode: z.string(), message: z.string() })
                .strict(),
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Convert to Employee (hr.edit AND hr.create — it creates an employee record): a REAL row in the shared master carrying name/email/phone + the requisition’s department & job title + the offer’s salary & start date (BR-HR-05-05/-06). Marks the requisition Filled at the openings count (Q-HR-05-2).',
    },
    scheduleInterview: {
        method: 'POST',
        path: '/api/hr/candidates/:candidateId/interview',
        pathParams: candidateParams,
        body: z.object({ assigneeId: z.uuid().optional() }).strict().optional(),
        responses: {
            201: z.object({ taskId: z.uuid(), message: z.string() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Creates a task in the SHARED engine (BR-HR-05-08): "Interview — <name>", meeting, due today 10:00, high priority, reminder 30 min — on the interviewer’s own list. hr.edit.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=recruitment.js.map